// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useState } from 'react';
import { Spinner } from '@loa/ui-components';
import { ProjectionAssumptionsEditor } from './ProjectionAssumptionsEditor';
import {
  projectionAssumptionsApi,
  type EffectiveAssumptions,
  type ProjectionAssumptions,
} from '../../api/projectionAssumptionsApi';

interface Props {
  /** Advisors with the ADMIN role also get the brokerage-level editor. */
  isAdmin: boolean;
}

/**
 * Settings-page section: the advisor edits their own projection-assumption
 * defaults; an advisor with the ADMIN role additionally edits the brokerage-level
 * defaults (which every advisor in the brokerage inherits). These sit below the
 * global default and above each relationship's own override.
 */
export function ProjectionAssumptionsSettings({ isAdmin }: Props) {
  const [advisor, setAdvisor] = useState<ProjectionAssumptions | null>(null);
  const [advisorInherited, setAdvisorInherited] = useState<EffectiveAssumptions | null>(null);
  const [brokerage, setBrokerage] = useState<ProjectionAssumptions | null>(null);
  const [brokerageInherited, setBrokerageInherited] = useState<EffectiveAssumptions | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const reads: Promise<unknown>[] = [
      projectionAssumptionsApi.getAdvisor().then(setAdvisor),
      projectionAssumptionsApi.getAdvisorInherited().then(setAdvisorInherited),
    ];
    if (isAdmin) {
      reads.push(projectionAssumptionsApi.getBrokerage().then(setBrokerage));
      reads.push(projectionAssumptionsApi.getBrokerageInherited().then(setBrokerageInherited));
    }
    Promise.all(reads).finally(() => setLoading(false));
  }, [isAdmin]);

  return (
    <div data-testid="projection-assumptions-settings" className="flex flex-col gap-lg">
      <div>
        <h3 className="text-[15px] font-semibold text-black mb-xs">My projection assumptions</h3>
        <p className="text-[13px] text-[#8A8A8A] mb-[12px]">
          Your defaults for cashflow &amp; Future-Values projections. Leave a field blank to inherit
          from the brokerage / platform default; a relationship can still override these.
        </p>
        {loading && !advisor && <Spinner />}
        {advisor && (
          <ProjectionAssumptionsEditor
            override={advisor}
            effective={advisorInherited}
            onSave={(body) => projectionAssumptionsApi.saveAdvisor(body).then(setAdvisor)}
            onClear={() => projectionAssumptionsApi.clearAdvisor().then(() => setAdvisor(blank()))}
            testId="advisor-assumptions-editor"
          />
        )}
      </div>

      {isAdmin && (
        <div data-testid="brokerage-assumptions-section">
          <h3 className="text-[15px] font-semibold text-black mb-xs">
            Brokerage projection assumptions
          </h3>
          <p className="text-[13px] text-[#8A8A8A] mb-[12px]">
            The house defaults every advisor in your brokerage inherits (unless they or a
            relationship override them). Editable because you have the ADMIN role.
          </p>
          {loading && !brokerage && <Spinner />}
          {brokerage && (
            <ProjectionAssumptionsEditor
              override={brokerage}
              effective={brokerageInherited}
              onSave={(body) => projectionAssumptionsApi.saveBrokerage(body).then(setBrokerage)}
              onClear={() => projectionAssumptionsApi.clearBrokerage().then(() => setBrokerage(blank()))}
              testId="brokerage-assumptions-editor"
            />
          )}
        </div>
      )}
    </div>
  );
}

function blank(): ProjectionAssumptions {
  return {
    priceInflationRate: null, earningsGrowthRate: null, statePensionAnnual: null,
    pensionDrawdownRate: null, expenditureAtRetirementFactor: null,
    defaultSavingsGrowthRate: null, defaultInvestmentGrowthRate: null,
    defaultPropertyGrowthRate: null, defaultCollectableGrowthRate: null,
    defaultSurplusSweepRate: null, childBenefitMonthly: null, horizonYears: null,
  };
}
